import dayjs from "dayjs";
import { AlertCircleIcon } from "lucide-react";
import { createContext, useContext, useEffect, useMemo, useRef, useState, type ComponentType, type FC, type PropsWithChildren, type ReactNode, type RefObject } from "react";
import React from "react";
import { cn } from "@/ds/utils";
import { Calendar } from "@/ds/shadcn/components/ui/calendar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuTrigger } from "@/ds/shadcn/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/ds/shadcn/components/ui/popover";
import { Spinner } from "@/ds/shadcn/components/ui/spinner";
import type { Option } from "@/lib/common/Option";
import type { PaginatedInfiniteQueryResult } from "@/lib/common/PaginatedInfiniteQueryResult";
import { screenSizeIsConsideredMobile, useScreenSize } from "@/lib/common/ScreenSizeContext";
import { useInfiniteScroll } from "@/lib/common/useInfiniteScroll";
import type { ProblemResponse } from "@/lib/http";

export type SpreadSheetRowComponentProps<T, U = undefined> = U extends undefined ? T : T & U;

type SpreadSheetProps<T, U = undefined> = {
	forQuery: PaginatedInfiniteQueryResult<T>;
	getKey: (item: T) => React.Key;
	headers: string[];
	headerClassNames?: string[];
	rowComponent: ComponentType<SpreadSheetRowComponentProps<T, U>>;
	additionalProps?: U;
	children?: ReactNode;
	scrollRef?: RefObject<HTMLElement | null>;
};

interface SpreadSheetContextValue {
	isMobile: boolean;
	headers: string[];
}

const SpreadSheetContext = createContext<SpreadSheetContextValue>({
	isMobile: false,
	headers: []
});

const SpreadSheetCellIndexContext = createContext<number>(-1);

export const SpreadSheet = <T, U = undefined>(props: SpreadSheetProps<T, U>): React.JSX.Element => {
	const {
		forQuery,
		headers,
		headerClassNames,
		rowComponent: RowComponent,
		getKey,
		additionalProps,
		children,
		scrollRef
	} = props;

	const screenSize = useScreenSize();
	const isMobile = screenSizeIsConsideredMobile(screenSize);

	const items = useMemo(
		() => forQuery.data?.pages.flatMap(x => x.results) ?? [],
		[forQuery.data]
	);

	const isEmpty = items.length === 0 && forQuery.isSuccess;
	if (isEmpty && children) {
		return <>{children}</>;
	}

	if (isMobile) {
		return (
			<SpreadSheetContext.Provider value={{ isMobile: true, headers }}>
				<div>
					{
						items.map(item => {
							const componentProps = { ...item, ...additionalProps } as SpreadSheetRowComponentProps<T, U>;
							return <RowComponent key={getKey(item)} {...componentProps} />;
						})
					}
					<SpreadSheetScroller colSpan={headers.length} forQuery={forQuery} scrollRef={scrollRef} />
				</div>
			</SpreadSheetContext.Provider>
		);
	}

	return (
		<SpreadSheetContext.Provider value={{ isMobile: false, headers }}>
			<div>
				<table className="w-full border-collapse table-fixed">
					<thead>
						<tr>
							{
								headers.map((label, i) => (
									<th
										key={i}
										className={cn(
											"border border-border bg-muted/40 px-3 py-1.5 text-left text-xs font-medium text-muted-foreground first:border-l-0 last:border-r-0",
											headerClassNames?.[i]
										)}>
										{label}
									</th>
								))
							}
						</tr>
					</thead>
					<tbody>
						{
							items.map(item => {
								const componentProps = { ...item, ...additionalProps } as SpreadSheetRowComponentProps<T, U>;
								return <RowComponent key={getKey(item)} {...componentProps} />;
							})
						}
						<SpreadSheetScroller colSpan={headers.length} forQuery={forQuery} scrollRef={scrollRef} />
					</tbody>
				</table>
			</div>
		</SpreadSheetContext.Provider>
	);
};

export interface SpreadSheetRowProps extends PropsWithChildren {
	isLoading?: boolean;
	error?: ProblemResponse | null;
	onClick?: () => void;
}

export const SpreadSheetRow: FC<SpreadSheetRowProps> = props => {
	const {
		children,
		isLoading,
		error,
		onClick
	} = props;

	const { isMobile, headers } = useContext(SpreadSheetContext);

	if (isMobile) {
		const childArray = React.Children.toArray(children);
		const firstChild = childArray[0];
		const lastChild = childArray[childArray.length - 1];
		const middleChildren = childArray.slice(1, -1);
		return (
			<>
				<div
					className={cn(
						isLoading && "pointer-events-none opacity-60",
						onClick && "cursor-pointer hover:bg-background-highlight transition-colors duration-150"
					)}
					onClick={onClick}>
					<div className="relative">
						<SpreadSheetCellIndexContext.Provider value={0}>
							{firstChild}
						</SpreadSheetCellIndexContext.Provider>
						<SpreadSheetCellIndexContext.Provider value={headers.length - 1}>
							{lastChild}
						</SpreadSheetCellIndexContext.Provider>
					</div>
					{
						middleChildren.map((child, i) => (
							<SpreadSheetCellIndexContext.Provider key={i} value={i + 1}>
								{child}
							</SpreadSheetCellIndexContext.Provider>
						))
					}
					{
						error && (
							<div className="border-t border-border px-3 py-2 text-xs text-destructive">
								<div className="flex items-center gap-2">
									<AlertCircleIcon className="h-3.5 w-3.5 shrink-0" />
									{error.problem.description}
								</div>
							</div>
						)
					}
				</div>
				<div className="h-4 border-t border-b border-border" />
			</>
		);
	}

	return (
		<>
			<tr
				className={cn(
					"even:bg-muted/50 dark:even:bg-muted/10",
					isLoading && "pointer-events-none opacity-60",
					onClick && "cursor-pointer hover:bg-background-highlight dark:even:hover:bg-background-highlight transition-colors duration-150"
				)}
				onClick={onClick}>
				{children}
			</tr>
			{
				error && (
					<tr>
						<td
							colSpan={100}
							className="border border-border px-3 py-2 text-xs text-destructive">
							<div className="flex items-center gap-2">
								<AlertCircleIcon className="h-3.5 w-3.5 shrink-0" />
								{error.problem.description}
							</div>
						</td>
					</tr>
				)
			}
		</>
	);
};

export interface SpreadSheetCellProps extends PropsWithChildren {
	className?: string;
	onClick?: () => void;
}

export const SpreadSheetCell: FC<SpreadSheetCellProps> = props => {
	const {
		children,
		className,
		onClick
	} = props;

	const { isMobile, headers } = useContext(SpreadSheetContext);
	const cellIndex = useContext(SpreadSheetCellIndexContext);

	if (isMobile) {
		const isFirst = cellIndex === 0;
		const isLast = cellIndex === headers.length - 1;

		if (isFirst) {
			return (
				<div
					className={cn("px-3 py-2 pr-12 text-sm", onClick && "cursor-pointer hover:bg-background-highlight transition-colors duration-150", className)}
					onClick={onClick}>
					{children}
				</div>
			);
		}

		if (isLast) {
			return (
				<div
					className={cn("absolute inset-y-0 right-3 flex items-center", onClick && "cursor-pointer hover:bg-background-highlight transition-colors duration-150", className)}
					onClick={onClick}>
					{children}
				</div>
			);
		}

		return (
			<div
				className={cn("flex items-center border-t border-border", onClick && "cursor-pointer hover:bg-background-highlight transition-colors duration-150")}
				onClick={onClick}>
				<div className="w-28 shrink-0 px-3 py-1.5 text-xs font-medium text-muted-foreground">
					{headers[cellIndex]}
				</div>
				<div className={cn("flex-1 px-3 py-1.5 text-sm min-w-0", className)}>
					{children}
				</div>
			</div>
		);
	}

	return (
		<td
			className={cn(
				"border border-border px-3 py-1.5 text-sm first:border-l-0 last:border-r-0",
				onClick && "cursor-pointer hover:bg-background-highlight transition-colors duration-150",
				className
			)}
			onClick={onClick}>
			{children}
		</td>
	);
};

export interface SpreadSheetEditableCellProps extends PropsWithChildren {
	className?: string;
	isEditing: boolean;
	isLoading?: boolean;
	onEditStart: () => void;
	onCancel?: () => void;
	editor: ReactNode;
}

export const SpreadSheetEditableCell: FC<SpreadSheetEditableCellProps> = props => {
	const {
		className,
		isEditing,
		isLoading,
		onEditStart,
		onCancel,
		editor,
		children
	} = props;

	useEffect(() => {
		if (!isEditing || !onCancel) {
			return;
		}
		const handler = (e: KeyboardEvent) => {
			if (e.key === "Escape") onCancel();
		};
		document.addEventListener("keydown", handler);
		return () => document.removeEventListener("keydown", handler);
	}, [isEditing, onCancel]);

	if (isEditing) {
		return (
			<SpreadSheetCell className={cn("p-0", className)}>
				{
					isLoading ? (
						<div className="flex justify-center px-3 py-1.5">
							<Spinner />
						</div>
					) : editor
				}
			</SpreadSheetCell>
		);
	}

	return (
		<SpreadSheetCell
			className={className}
			onClick={onEditStart}>
			{children}
		</SpreadSheetCell>
	);
};

const useEditingState = (isSuccess: boolean) => {
	const [isEditing, setIsEditing] = useState(false);

	useEffect(() => {
		if (!isSuccess) {
			return;
		}
		setIsEditing(false);
	}, [isSuccess]);

	return {
		isEditing,
		startEditing: () => setIsEditing(true),
		cancel: () => setIsEditing(false)
	};
};

const useInlineCellEdit = <T,>(value: T, isSuccess: boolean, serialize: (value: T) => string, parse: (draft: string) => T | null, onCommit: (value: T) => void) => {
	const { isEditing, startEditing, cancel } = useEditingState(isSuccess);
	const [draft, setDraft] = useState("");

	return {
		isEditing,
		draft,
		setDraft,
		startEditing: () => {
			setDraft(serialize(value));
			startEditing();
		},
		commit: () => {
			const parsed = parse(draft);
			if (parsed === null || parsed === value) {
				cancel();
				return;
			}
			onCommit(parsed);
		},
		cancel
	};
};

const defaultParseDecimal = (draft: string): number | null => {
	const n = parseFloat(draft);
	return isNaN(n) ? null : n;
};

export interface SpreadSheetEditableNumberCellProps extends PropsWithChildren {
	className?: string;
	value: number;
	isLoading?: boolean;
	isSuccess: boolean;
	inputMode?: "decimal" | "numeric";
	parse?: (draft: string) => number | null;
	onCommit: (value: number) => void;
}

export const SpreadSheetEditableNumberCell: FC<SpreadSheetEditableNumberCellProps> = props => {
	const {
		className,
		value,
		isLoading,
		isSuccess,
		inputMode = "decimal",
		parse = defaultParseDecimal,
		onCommit,
		children
	} = props;

	const edit = useInlineCellEdit(value, isSuccess, v => v.toString(), parse, onCommit);

	return (
		<SpreadSheetEditableCell
			className={className}
			isEditing={edit.isEditing}
			isLoading={isLoading}
			onEditStart={edit.startEditing}
			editor={
				<input
					type="text"
					inputMode={inputMode}
					value={edit.draft}
					autoFocus
					className="w-full px-3 py-1.5 text-sm bg-transparent outline-none"
					onChange={e => edit.setDraft(e.target.value)}
					onKeyDown={e => {
						if (e.key === "Enter") edit.commit();
						if (e.key === "Escape") edit.cancel();
					}}
					onBlur={edit.cancel} />
			}>
			{children}
		</SpreadSheetEditableCell>
	);
};

export interface SpreadSheetEditableOptionCellProps<T extends string> extends PropsWithChildren {
	className?: string;
	value: T;
	isLoading?: boolean;
	options: Option<T>[];
	onCommit: (value: T) => void;
}

export const SpreadSheetEditableOptionCell = <T extends string,>(props: SpreadSheetEditableOptionCellProps<T>): React.JSX.Element => {
	const {
		className,
		value,
		isLoading,
		options,
		onCommit,
		children
	} = props;

	const { isMobile } = useContext(SpreadSheetContext);

	return (
		<SpreadSheetCell className={cn("p-0", !isMobile && "relative", className)}>
			<DropdownMenu>
				<DropdownMenuTrigger
					disabled={isLoading}
					className={cn(
						"text-sm outline-none cursor-pointer hover:bg-background-highlight transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-60",
						isMobile ? "w-full px-3 py-1.5 text-left" : "absolute inset-0 flex items-center px-3"
					)}>
					{
						isLoading ? (
							<div className="flex justify-center">
								<Spinner />
							</div>
						) : children
					}
				</DropdownMenuTrigger>
				<DropdownMenuContent align="start">
					<DropdownMenuRadioGroup
						value={value}
						onValueChange={newValue => {
							if (newValue !== value) onCommit(newValue as T);
						}}>
						{
							options.map(opt => (
								<DropdownMenuRadioItem key={opt.value} value={opt.value}>
									{opt.label}
								</DropdownMenuRadioItem>
							))
						}
					</DropdownMenuRadioGroup>
				</DropdownMenuContent>
			</DropdownMenu>
		</SpreadSheetCell>
	);
};

export interface SpreadSheetEditableDateCellProps extends PropsWithChildren {
	className?: string;
	value: string;
	isLoading?: boolean;
	isSuccess: boolean;
	onCommit: (value: string) => void;
}

export const SpreadSheetEditableDateCell: FC<SpreadSheetEditableDateCellProps> = props => {
	const {
		className,
		value,
		isLoading,
		isSuccess,
		onCommit,
		children
	} = props;

	const { isMobile } = useContext(SpreadSheetContext);
	const [open, setOpen] = useState(false);

	useEffect(() => {
		if (!isSuccess) {
			return;
		}
		setOpen(false);
	}, [isSuccess]);

	return (
		<SpreadSheetCell className={cn("p-0", !isMobile && "relative", className)}>
			<Popover open={open} onOpenChange={setOpen}>
				<PopoverTrigger
					disabled={isLoading}
					className={cn(
						"text-sm outline-none cursor-pointer hover:bg-background-highlight transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-60",
						isMobile ? "w-full px-3 py-1.5 text-left" : "absolute inset-0 flex items-center px-3"
					)}>
					{
						isLoading ? (
							<div className="flex justify-center">
								<Spinner />
							</div>
						) : children
					}
				</PopoverTrigger>
				<PopoverContent className="w-auto p-0" align="start">
					<Calendar
						mode="single"
						selected={dayjs(value).toDate()}
						onSelect={date => {
							if (date) onCommit(dayjs(date).format("YYYY-MM-DD"));
						}} />
				</PopoverContent>
			</Popover>
		</SpreadSheetCell>
	);
};

interface SpreadSheetScrollerProps<T> {
	forQuery: PaginatedInfiniteQueryResult<T>;
	colSpan: number;
	scrollRef?: RefObject<HTMLElement | null>;
}

const SpreadSheetScroller = <T,>(props: SpreadSheetScrollerProps<T>): React.JSX.Element => {
	const {
		forQuery,
		colSpan,
		scrollRef
	} = props;

	const ref = useRef<HTMLDivElement | null>(null);
	const showSpinner = useInfiniteScroll(ref, forQuery, scrollRef);

	const { isMobile } = useContext(SpreadSheetContext);

	if (isMobile) {
		return (
			<div className={cn("p-6", !showSpinner && "hidden")}>
				<div ref={ref} className="flex justify-center">
					<Spinner />
				</div>
			</div>
		);
	}

	return (
		<tr className={cn(!showSpinner && "hidden")}>
			<td colSpan={colSpan} className="border border-border p-6">
				<div ref={ref} className="flex justify-center">
					<Spinner />
				</div>
			</td>
		</tr>
	);
};
